import React, { useContext, useState } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AuthContext } from "../../context/AuthContext";
import AdminLayout from "../../layouts/AdminLayout";
import { getLibroCompras, downloadLibroComprasXlsx } from "../../controllers/api_compras";
import { fmt, handleApiError } from "./comprasShared";
import {
  Card,
  PageHeader,
  Field,
  TextInput,
  BtnPrimary,
  BtnGhost,
  Table,
  Th,
  Td,
} from "./ui";

const periodoActual = () => new Date().toISOString().slice(0, 7);

export default function LibroComprasPage() {
  const { user } = useContext(AuthContext);
  const [periodo, setPeriodo] = useState(periodoActual());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const buscar = async () => {
    setLoading(true);
    try { setData(await getLibroCompras({ periodo })); }
    catch (e) { handleApiError(e); setData(null); }
    finally { setLoading(false); }
  };

  const descargar = async () => {
    try {
      const blob = await downloadLibroComprasXlsx({ periodo });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `Libro_IVA_Compras_${periodo}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { handleApiError(e); }
  };

  const r = data?.resumen;
  const lineas = data?.lineas || [];

  const resumenCards = r
    ? [
        { label: "Base imponible", val: r.total_base, icon: "fa-coins", color: "text-sirius-600", ring: "bg-sirius-50" },
        { label: "Crédito fiscal", val: r.total_credito_fiscal, icon: "fa-file-invoice-dollar", color: "text-emerald-600", ring: "bg-emerald-50" },
        { label: "IVA retenido", val: r.total_iva_retenido, icon: "fa-hand-holding-dollar", color: "text-amber-600", ring: "bg-amber-50" },
        { label: "IGTF", val: r.total_igtf, icon: "fa-percent", color: "text-spark-500", ring: "bg-sirius-50" },
      ]
    : [];

  return (
    <AdminLayout nombreUsuario={user?.nombre || "Usuario"}>
      <div className="mt-8 px-4 md:px-8">
        <div className="mx-auto w-full">
          <Card>
            {/* Header con título y acciones */}
            <PageHeader
              title="Libro de IVA Compras"
              subtitle="Consulta y descarga el libro de compras por período fiscal."
              icon="fa-book"
              actions={
                data && (
                  <div className="relative group inline-block">
                    <BtnGhost icon="fa-file-excel" onClick={descargar}>
                      Descargar Excel
                    </BtnGhost>
                  </div>
                )
              }
            />

            {/* Barra de filtros: período + botón consultar */}
            <div className="px-4 md:px-6 py-4 border-b border-slate-100">
              <div className="flex flex-wrap items-end -mx-4">
                <Field label="Período (YYYY-MM)" className="w-full sm:w-auto px-4">
                  <TextInput
                    type="month"
                    value={periodo}
                    onChange={(v) => setPeriodo(v)}
                    className="sm:min-w-[14rem]"
                  />
                </Field>
                <div className="px-4 mb-3">
                  <BtnPrimary icon="fa-magnifying-glass" onClick={buscar} disabled={loading}>
                    {loading ? "Consultando..." : "Consultar"}
                  </BtnPrimary>
                </div>
              </div>
            </div>

            {/* Tarjetas resumen */}
            {r && (
              <div className="px-4 md:px-6 py-5 border-b border-slate-100">
                <div className="flex flex-wrap -mx-3">
                  {resumenCards.map((c) => (
                    <div key={c.label} className="w-1/2 lg:w-1/4 px-3 mb-4 lg:mb-0">
                      <div className="relative bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-sirius transition-shadow px-5 py-4 h-full flex items-center gap-4">
                        <span
                          className={`inline-flex items-center justify-center w-11 h-11 rounded-xl ${c.ring} ${c.color} shrink-0`}
                        >
                          <i className={`fas ${c.icon} text-lg`} aria-hidden="true" />
                        </span>
                        <div className="min-w-0">
                          <p className="text-[11px] uppercase tracking-[0.12em] font-semibold text-ink-500 truncate">
                            {c.label}
                          </p>
                          <p className="text-lg md:text-xl font-display font-bold text-ink-900 mt-0.5">
                            {fmt(c.val)}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Tabla de líneas del libro */}
            {data && (
              <Table className="min-w-[1100px]">
                <thead>
                  <tr>
                    <Th>#</Th>
                    <Th>Fecha</Th>
                    <Th>Tipo</Th>
                    <Th>Nº Doc</Th>
                    <Th>Nº Control</Th>
                    <Th>Comprobante</Th>
                    <Th>RIF</Th>
                    <Th>Razón social</Th>
                    <Th className="text-right">Total c/IVA</Th>
                    <Th className="text-right">Sin créd.</Th>
                    <Th className="text-right">Base 16%</Th>
                    <Th className="text-right">IVA 16%</Th>
                    <Th className="text-right">IVA retenido</Th>
                    <Th className="text-right">IGTF</Th>
                  </tr>
                </thead>
                <tbody>
                  {lineas.length === 0 && (
                    <tr>
                      <Td colSpan={14} className="text-center text-blueGray-400 italic py-8">
                        Sin operaciones en el período.
                      </Td>
                    </tr>
                  )}
                  {lineas.map((l) => (
                    <tr key={l.nro} className="hover:bg-sirius-50/40 transition-colors">
                      <Td>{l.nro}</Td>
                      <Td>{l.fecha}</Td>
                      <Td>{l.tipo}</Td>
                      <Td>{l.numero_documento || "—"}</Td>
                      <Td>{l.numero_control || "—"}</Td>
                      <Td>{l.numero_comprobante || "—"}</Td>
                      <Td>{l.rif}</Td>
                      <Td>{l.razon_social}</Td>
                      <Td className="text-right tabular-nums">{fmt(l.total_con_iva)}</Td>
                      <Td className="text-right tabular-nums">{fmt(l.sin_credito)}</Td>
                      <Td className="text-right tabular-nums">{fmt(l.base_16)}</Td>
                      <Td className="text-right tabular-nums">{fmt(l.iva_16)}</Td>
                      <Td className="text-right tabular-nums">{fmt(l.iva_retenido)}</Td>
                      <Td className="text-right tabular-nums">{fmt(l.igtf)}</Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card>
        </div>
        <ToastContainer position="bottom-right" autoClose={3000} />
      </div>
    </AdminLayout>
  );
}
